/**
 * THE RANGE — fifteen seconds behind the saloon, before any score is at stake.
 *
 * The panel's UX seat made this a gate condition: a first-time player must be
 * able to succeed unaided (ROUNDTABLE.md, A-25). Motion aiming is an unfamiliar
 * control scheme, and dropping somebody into a contested wave with seven
 * strangers to learn it is how you produce a player who fires six shells at
 * nothing and closes the tab.
 *
 * It runs entirely on the client. Nothing here touches the server, nothing here
 * scores, and hit resolution is local — which is fine precisely BECAUSE nothing
 * is at stake. It is the one place in this codebase where the client is allowed
 * to decide whether something was hit.
 */

import { hitTest, makeSpawn, type DeerRecipe, type DeerSpawn } from '@shared/deer.js';
import { mulberry32 } from '@shared/rng.js';
import { SITES } from '@shared/sites.js';
import { clock } from '../net/clock.js';
import { audio } from './audio.js';
import { world } from './world.js';

/** Slow, near, broadside. Everything the first shot should be. */
const LESSON: DeerRecipe[] = [
  { kind: 'buck', rack: 8, speed: 0.34, zMin: 0.18, zMax: 0.22, leap: 0, weave: 0, delay: 1.4 },
  { kind: 'buck', rack: 10, speed: 0.46, zMin: 0.3, zMax: 0.36, leap: 0, weave: 0.1, delay: 6.5 },
  // The third one is a doe, on purpose. Better to learn the doe rule here, for
  // nothing, than in wave 2 for five hundred points and in front of everybody.
  { kind: 'doe', rack: 0, speed: 0.5, zMin: 0.26, zMax: 0.34, leap: 0, weave: 0.12, delay: 11.5 },
];

export interface RangeState {
  hits: number;
  shots: number;
  doeShot: boolean;
  done: boolean;
}

export class Range {
  readonly state: RangeState = { hits: 0, shots: 0, doeShot: false, done: false };
  private spawns: DeerSpawn[] = [];
  private endsAt = 0;

  start() {
    const now = clock.now();
    const rng = mulberry32(0x2bc5a100);
    const startsAt = now + 1600;
    let id = 1;
    this.spawns = LESSON.map((r) => makeSpawn(id++, r, startsAt, rng));
    this.endsAt = Math.max(...this.spawns.map((s) => s.tExit)) + 800;

    world.reset();
    world.waveIndex = 0;
    world.kind = 'hunt';
    world.site = SITES[0]!;
    world.startsAt = startsAt;
    world.endsAt = this.endsAt;
    world.deer = this.spawns;
    world.varmints = [];
    world.maxShells = 6;
    world.shells = 6;
    world.active = true;

    this.state.hits = 0;
    this.state.shots = 0;
    this.state.doeShot = false;
    this.state.done = false;

    world.note('Point the phone. Tap anywhere to fire.', 'info');
  }

  /** Local hit resolution. Returns true if the range is now finished. */
  fire(sx: number, sy: number): boolean {
    const t = clock.now();
    if (!world.active || t < world.startsAt) return false;

    if (world.reloadUntil && t < world.reloadUntil) return false;
    if (world.reloadUntil && t >= world.reloadUntil) {
      world.reloadUntil = 0;
      world.shells = world.maxShells;
    }
    if (world.shells <= 0) {
      audio.play('dryfire');
      return false;
    }

    world.shells -= 1;
    this.state.shots += 1;
    if (world.shells === 0) {
      world.reloadUntil = t + 850;
      audio.play('rack');
    }

    audio.play('shot');
    let hitAny: DeerSpawn | null = null;
    let zone: ReturnType<typeof hitTest> = null;
    for (const d of this.spawns) {
      if (t < d.t0 || t >= d.tExit || world.dead.has(d.id)) continue;
      const z = hitTest(d, t, sx, sy);
      if (z) {
        hitAny = d;
        zone = z;
        break;
      }
    }

    world.onShot(sx, sy, 0, !!hitAny, true);

    if (!hitAny || !zone) return this.check(t);

    if (hitAny.kind === 'doe') {
      this.state.doeShot = true;
      world.onDoe(hitAny.id, 'You', 0, 500, true);
      world.note('That was a doe. In a real hunt you would be out for the wave.', 'doe');
      audio.play('doe');
    } else {
      this.state.hits += 1;
      world.onKill(hitAny.id, 'You', 0, zone, 0, hitAny.rack, t, true);
      audio.play('hit');
      navigator.vibrate?.(20);
    }
    return this.check(t);
  }

  /** Called from the frame loop as well, so it ends on time with no input. */
  check(t = clock.now()): boolean {
    if (this.state.done) return true;
    // The doe is the last lesson, so once it is resolved or gone, so is this.
    const allResolved = this.spawns.every((d) => world.dead.has(d.id) || t >= d.tExit);
    if (t >= this.endsAt || allResolved) {
      this.state.done = true;
      world.active = false;
      return true;
    }
    return false;
  }

  stop() {
    this.state.done = true;
    world.active = false;
    world.reset();
  }
}
